import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Card, Input, InputNumber, Modal, Select, Space, TimePicker, Typography } from 'antd';
import dayjs from 'dayjs';

import ConditionBuilder from '../common/ConditionBuilder';
import ScheduleFields, { browserTimezone, parseStoredTime } from './ScheduleFields';
import TokenInput from '../common/TokenInput';
import { InputSelect } from '../Input';
import { buildCardHierarchyOptions } from '../cards/CardVisuals';
import { buildGeneratorTokens } from '../../utils/automationTokens';
import { createAutomation, patchAutomation } from '../../services/automations';
import { normalizeSchedule } from '../../utils/automationSchedule';
import { TIME_FORMAT_PATTERNS } from '../../utils/dateDisplay';
import useTimeFormat from '../../hooks/useTimeFormat';
import { normalizeId } from '../../utils/ids';
import { CLOSE, PLUS } from '../icons';
import { isFileElement } from '../../utils/fileValues';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';
import LookupSourceField from './LookupSourceField';
import { LOOKUP_SOURCE_REFUSED, lookupSourceOptions } from '../../utils/lookupSources';

/**
 * Create/edit one generator (`kind: 'generate-items'`) — the rule that MAKES
 * the work instead of only checking for it. Tomorrow's med passes, next
 * week's walk-arounds, the month's charges.
 *
 * The one idea a manager has to hold while using this editor is that there
 * are TWO cadences, and they answer different questions:
 *
 *   "How often does this run?"   the schedule — nightly at 23:30
 *   "What does it make?"         the occurrences — 08:00, 12:00 and 17:00
 *
 * So the sections are ordered as those questions: what to generate FROM, what
 * occurrences look like, when the generator runs, and what each occurrence
 * becomes. Progressive, like every other rule editor here: a section appears
 * once the one before it is answerable.
 *
 * Two API rules shape the controls rather than being re-implemented:
 * `{{trigger.*}}` does not exist for a generator (there is no trigger item on
 * a clock, so the token menu offers only `{{each.*}}` and `{{slot.*}}`), and
 * the dedupe key is REQUIRED — a generator without one duplicates the day on
 * the first retried run, so the picker is right beside the values it keys on
 * and the save refuses without it rather than letting the API say it later.
 */

const { Text } = Typography;

const STORED_TIME_FORMAT = 'HH:mm';
const MAX_SLOTS = 24;
const MAX_HORIZON_DAYS = 31;
const MAX_SOURCES = 500;

let keySeq = 0;
const nextKey = () => `g${ ++keySeq }`;

const elementLabelOf = el => el?.label?.singular || el?.name;
const namedElements = elements => ( elements || [] ).filter( el => el?.name );
// Never a file element (PR 10b): a generated item cannot be handed a file
// (the API refuses one, 400 file_not_settable). Nor a "Me too" count
// (KPR-390): a new item starts with nobody's confirmation (400
// confirmations_not_settable).
export const writableElements = elements => namedElements( elements ).filter( el => ! el?.options?.drivenFrom && ! isFileElement( el ) && el?.dataType !== 'confirmations' );
const cardLookupElements = elements => namedElements( elements ).filter( el => el?.dataType === 'card-lookup' );

const rowsFromObject = obj => Object.entries( obj || {} ).map( ( [ name, value ] ) => ( {
	key: nextKey(),
	name,
	// A list stays a list: the records a lookup's value names (KPR-1104).
	value: value === null || value === undefined ? '' : ( Array.isArray( value ) ? value : `${ value }` ),
} ) );

const rowsToObject = rows => {
	const out = {};
	( rows || [] ).forEach( row => {
		const name = ( row?.name || '' ).trim();
		if ( ! name ) return;
		out[ name ] = row?.value ?? '';
	} );
	return out;
};

const emptyRow = () => ( { key: nextKey(), name: '', value: '' } );
const emptySlot = () => ( { key: nextKey(), label: '', time: '' } );

const slotsFrom = list => ( Array.isArray( list ) && list.length
	? list.map( slot => ( { key: nextKey(), label: slot?.label || '', time: slot?.time || '' } ) )
	: [ { key: nextKey(), label: 'Morning', time: '08:00' } ] );

export default function GenerateItemsRuleEditor( {
	open,
	collectionId,
	rule,
	collectionCards,
	cardsById,
	getElementsForCard,
	onClose,
	onSaved,
} ) {

	const { message } = App.useApp();
	const timeFormat = useTimeFormat();
	const timePattern = TIME_FORMAT_PATTERNS[ timeFormat ] || TIME_FORMAT_PATTERNS[ '12h' ];

	const [ name, setName ] = useState( '' );
	const [ schedule, setSchedule ] = useState( () => ( { every: 1, unit: 'day', time: '23:30', timezone: browserTimezone() } ) );

	const [ sourceCardId, setSourceCardId ] = useState( '' );
	const [ sourceKql, setSourceKql ] = useState( '' );

	const [ slots, setSlots ] = useState( slotsFrom( null ) );
	const [ fromElement, setFromElement ] = useState( '' );
	const [ startElement, setStartElement ] = useState( '' );
	const [ endElement, setEndElement ] = useState( '' );
	const [ horizonDays, setHorizonDays ] = useState( 1 );
	const [ maxSources, setMaxSources ] = useState( null );

	const [ targetCardId, setTargetCardId ] = useState( '' );
	const [ rows, setRows ] = useState( [ emptyRow() ] );
	const [ linkElementName, setLinkElementName ] = useState( '' );
	const [ dedupeOn, setDedupeOn ] = useState( [] );

	const [ saving, setSaving ] = useState( false );

	// Everything the editor does NOT own on a stored rule rides through a save
	// untouched — the API stores `generate` verbatim, so a no-op save must not
	// erase a key a later release added.
	const [ extras, setExtras ] = useState( {} );

	useEffect( () => {
		if ( ! open ) return;
		const stored = rule?.generate || {};
		const occurrences = stored.occurrences || {};
		const create = stored.create || {};
		setName( rule?.name || '' );
		setSchedule( ( () => {
			const normalized = normalizeSchedule( rule?.trigger?.schedule );
			const { anchor, ...rest } = normalized;
			return { time: '23:30', timezone: browserTimezone(), ...rest };
		} )() );
		setSourceCardId( normalizeId( stored.source?.card_id ) || '' );
		setSourceKql( stored.source?.kql || '' );
		setSlots( slotsFrom( occurrences.slots ) );
		setFromElement( occurrences.from?.element || '' );
		setStartElement( occurrences.start?.element || '' );
		setEndElement( occurrences.end?.element || '' );
		setHorizonDays( Number.isFinite( Number( stored.horizon?.days ) ) ? Number( stored.horizon.days ) : 1 );
		setMaxSources( Number.isFinite( Number( stored.max ) ) ? Number( stored.max ) : null );
		setTargetCardId( normalizeId( create.card_id ) || '' );
		setRows( create.elements ? rowsFromObject( create.elements ) : [ emptyRow() ] );
		setLinkElementName( create.linkElementName || '' );
		setDedupeOn( Array.isArray( create.dedupe?.on ) ? create.dedupe.on : [] );
		const { source, occurrences: o, horizon, max, create: c, ...rest } = stored;
		setExtras( rest );
	}, [ open, rule ] );

	const cardOptions = useMemo( () => buildCardHierarchyOptions( collectionCards ), [ collectionCards ] );

	const elementsFor = useCallback(
		id => ( id && getElementsForCard ? ( getElementsForCard( id ) || [] ) : [] ),
		[ getElementsForCard ]
	);

	const sourceElements = elementsFor( sourceCardId );
	const targetElements = elementsFor( targetCardId );

	const tokens = useMemo( () => buildGeneratorTokens( sourceElements ), [ sourceElements ] );

	// Which slots an individual source item uses can be read off a choice or
	// text element; date bounds off a date element. The pickers offer only the
	// types the API accepts, so a refusal never arrives after the save.
	const fromOptions = useMemo( () => namedElements( sourceElements )
		.filter( el => [ 'choice', 'text-small', 'text-large' ].includes( el.dataType ) )
		.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) ), [ sourceElements ] );
	const dateOptions = useMemo( () => namedElements( sourceElements )
		.filter( el => el.dataType === 'date' || el.dataType === 'date-time' )
		.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) ), [ sourceElements ] );

	const elementOptions = useMemo( () => writableElements( targetElements )
		.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) ), [ targetElements ] );
	// The generated record's lookups (KPR-1104): filled from a link or chosen
	// records, never typed — the last definition of a name wins.
	const lookupTargets = useMemo( () => {
		const byName = new Map();
		writableElements( targetElements ).forEach( el => byName.set( el.name, el ) );
		return new Map( [ ...byName ].filter( ( [ , el ] ) => el?.dataType === 'card-lookup' ) );
	}, [ targetElements ] );

	const linkOptions = useMemo( () => cardLookupElements( targetElements )
		.filter( el => normalizeId( el?.options?.lookupCardId ) === sourceCardId )
		.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) ), [ targetElements, sourceCardId ] );

	// The dedupe key may only name what this rule writes: a key on a blank
	// element would match every blank row and suppress every create.
	const dedupeOptions = useMemo( () => {
		const written = rows.map( row => ( row?.name || '' ).trim() ).filter( Boolean );
		if ( linkElementName ) written.push( linkElementName );
		return Array.from( new Set( written ) ).map( value => ( {
			value,
			label: elementLabelOf( targetElements.find( el => el?.name === value ) ) || value,
		} ) );
	}, [ rows, linkElementName, targetElements ] );

	const patchRow = ( key, patch ) => setRows( prev => prev.map( row => ( row.key === key ? { ...row, ...patch } : row ) ) );
	const patchSlot = ( key, patch ) => setSlots( prev => prev.map( slot => ( slot.key === key ? { ...slot, ...patch } : slot ) ) );

	const sourceCardName = cardsById?.[ sourceCardId ]?.name || 'source';
	const perDay = slots.filter( slot => slot.label && slot.time ).length;

	// An element a condition could mean several of (400 `ambiguous_element`,
	// element ids step 3): the person picks one, or any of these cards, and
	// the rule is saved again with the pick (`elementChoices`).
	// …answering exactly the source it was made for: any change forgets it.
	const elementPicks = useElementChoices( JSON.stringify( [ sourceCardId, sourceKql ] ) );
	const resetElementPicks = elementPicks.reset;
	// Each opening starts without picks.
	useEffect( () => { if ( open ) resetElementPicks(); }, [ open, resetElementPicks ] );

	const handleSave = async () => {
		const cleanSlots = slots
			.map( slot => ( { label: ( slot.label || '' ).trim(), time: ( slot.time || '' ).trim() } ) )
			.filter( slot => slot.label && slot.time );
		if ( ! cleanSlots.length ) {
			message.error( 'Give the generator at least one occurrence — a name and a time of day.' );
			return;
		}
		if ( ! dedupeOn.length ) {
			message.error( 'Pick what identifies one record, or running the generator twice writes the work twice.' );
			return;
		}
		if ( schedule.unit === 'week' && ! ( schedule.daysOfWeek || [] ).length ) {
			message.error( 'Pick at least one weekday for a weekly generator.' );
			return;
		}

		const { anchor, ...cadence } = schedule;
		const payload = {
			name: name.trim(),
			kind: 'generate-items',
			trigger: { type: 'schedule', schedule: cadence },
			generate: {
				...extras,
				source: {
					card_id: sourceCardId,
					...( sourceKql.trim() ? { kql: sourceKql.trim() } : {} ),
				},
				occurrences: {
					slots: cleanSlots,
					timezone: schedule.timezone || browserTimezone(),
					...( fromElement ? { from: { element: fromElement } } : {} ),
					...( startElement ? { start: { element: startElement } } : {} ),
					...( endElement ? { end: { element: endElement } } : {} ),
				},
				horizon: { days: Number( horizonDays ) || 1 },
				...( Number.isFinite( Number( maxSources ) ) && maxSources ? { max: Number( maxSources ) } : {} ),
				create: {
					card_id: targetCardId,
					elements: rowsToObject( rows ),
					...( linkElementName ? { linkElementName } : {} ),
					dedupe: { on: dedupeOn },
				},
			},
			actions: Array.isArray( rule?.actions ) ? rule.actions : [],
		};

		setSaving( true );
		try {
			// merge:false — the payload above is the complete generator, so the
			// stored `generate` is REPLACED. Its optional members (`max`, the
			// source KQL, the from/start/end elements) are left OUT when they
			// are cleared and none of them has a null the API accepts, so
			// replacement is the only thing that actually clears them. Ignored
			// by an API that predates the flag.
			if ( rule?._id ) await patchAutomation( collectionId, normalizeId( rule._id ), elementPicks.withChoices( { ...payload, merge: false } ) );
			else await createAutomation( collectionId, elementPicks.withChoices( payload ) );
			elementPicks.reset();
			message.success( rule?._id ? 'Generator saved.' : 'Generator created.' );
			onSaved?.();
		}
		catch ( err ) {
			// Verbatim: the API's 400s are field-level and written for humans.
			if ( elementPicks.take( err ) ) return;
			message.error( ( err?.response?.data?.code === LOOKUP_SOURCE_REFUSED
				? 'A lookup can only be filled from a link or a chosen record. Pick a source or a record for the flagged value, then save again.'
				: null ) || err?.response?.data?.message || 'Could not save the generator.' );
		}
		finally {
			setSaving( false );
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			onOk={handleSave}
			okText='Save'
			confirmLoading={saving}
			title={rule?._id ? 'Edit generator' : 'New generator'}
			width={760}
			destroyOnClose
		>
			<Space direction='vertical' size={12} style={{ width: '100%' }}>

				<div>
					<label htmlFor='generatorName' className='d-block'>Name</label>
					<Input
						id='generatorName'
						size='large'
						value={name}
						placeholder='What this generator makes, in a few words'
						onChange={e => setName( e.target.value )}
					/>
				</div>

				<Card size='small' title='What it generates from'>
					<InputSelect
						elementName='generatorSourceCard'
						elementLabel='For each'
						value={sourceCardId || undefined}
						onChange={( n, value ) => {
							setSourceCardId( value || '' );
							setFromElement( '' );
							setStartElement( '' );
							setEndElement( '' );
							setLinkElementName( '' );
						}}
						options={cardOptions}
						placeholder='Which records drive the schedule?'
						allowClear={false}
					/>
					{sourceCardId ? (
						<div style={{ marginTop: 8 }}>
							<Text strong className='d-block'>Only these</Text>
							<ConditionBuilder
								value={sourceKql}
								onChange={setSourceKql}
								elements={sourceElements}
								collectionId={collectionId}
							/>
							<Text type='secondary'>
								Optional. No &#123;&#123; &#125;&#125; tokens here — a generator has no trigger item.
							</Text>
						</div>
					) : null}
				</Card>

				{sourceCardId ? (
					<Card size='small' title='What the occurrences are'>
						<Text type='secondary' className='d-block' style={{ marginBottom: 8 }}>
							Name each moment of the day this generates a record for.
						</Text>
						{slots.map( slot => (
							<Space key={slot.key} align='center' style={{ display: 'flex', marginBottom: 8 }}>
								<Input
									style={{ width: 200 }}
									value={slot.label}
									placeholder='Name (e.g. Morning)'
									aria-label='Occurrence name'
									onChange={e => patchSlot( slot.key, { label: e.target.value } )}
								/>
								<TimePicker
									style={{ width: 140 }}
									format={timePattern}
									needConfirm={false}
									allowClear={false}
									aria-label={`Time for ${ slot.label || 'this occurrence' }`}
									value={parseStoredTime( slot.time )}
									onChange={next => patchSlot( slot.key, { time: next ? dayjs( next ).format( STORED_TIME_FORMAT ) : '' } )}
								/>
								{slots.length > 1 ? (
									<Button
										type='text'
										danger
										icon={<CLOSE />}
										aria-label='Remove this occurrence'
										onClick={() => setSlots( prev => prev.filter( s => s.key !== slot.key ) )}
									/>
								) : null}
							</Space>
						) )}
						{slots.length < MAX_SLOTS ? (
							<Button size='small' type='dashed' icon={<PLUS />} onClick={() => setSlots( prev => [ ...prev, emptySlot() ] )}>
								Add an occurrence
							</Button>
						) : null}

						{fromOptions.length ? (
							<div style={{ marginTop: 12 }}>
								<label htmlFor='generatorFromElement' className='d-block'>Which occurrences each {sourceCardName} uses</label>
								<Select
									id='generatorFromElement'
									style={{ width: '100%' }}
									allowClear
									value={fromElement || undefined}
									placeholder='All of them (or pick the element that names them)'
									options={fromOptions}
									onChange={value => setFromElement( value || '' )}
								/>
								<Text type='secondary'>
									The element&apos;s values are matched to the names above, so the cadence can
									live on the record instead of on this rule.
								</Text>
							</div>
						) : null}

						{dateOptions.length ? (
							<Space style={{ marginTop: 12 }} wrap align='start'>
								<div>
									<label htmlFor='generatorStartElement' className='d-block'>Starts on</label>
									<Select
										id='generatorStartElement'
										style={{ width: 220 }}
										allowClear
										value={startElement || undefined}
										placeholder='No start bound'
										options={dateOptions}
										onChange={value => setStartElement( value || '' )}
									/>
								</div>
								<div>
									<label htmlFor='generatorEndElement' className='d-block'>Ends on</label>
									<Select
										id='generatorEndElement'
										style={{ width: 220 }}
										allowClear
										value={endElement || undefined}
										placeholder='No end bound'
										options={dateOptions}
										onChange={value => setEndElement( value || '' )}
									/>
								</div>
							</Space>
						) : null}
					</Card>
				) : null}

				{sourceCardId ? (
					<Card size='small' title='When it runs'>
						<Space direction='vertical' size={12} style={{ width: '100%' }}>
							<ScheduleFields
								value={schedule}
								onChange={setSchedule}
								idPrefix='generator'
								everyLabel='Run every'
								timeLabel='Run at'
								timezoneHint='Both the run time and every occurrence above are read in this zone.'
							/>
							<Space align='start' wrap>
								<div>
									<label htmlFor='generatorHorizon' className='d-block'>Generate ahead</label>
									<InputNumber
										id='generatorHorizon'
										inputMode='numeric'
										min={1}
										max={MAX_HORIZON_DAYS}
										value={horizonDays}
										addonAfter='days'
										onChange={value => setHorizonDays( value )}
									/>
								</div>
								<div>
									<label htmlFor='generatorMax' className='d-block'>At most</label>
									<InputNumber
										id='generatorMax'
										inputMode='numeric'
										min={1}
										max={MAX_SOURCES}
										value={maxSources}
										placeholder='200'
										addonAfter={`${ sourceCardName } records`}
										onChange={value => setMaxSources( value )}
									/>
								</div>
							</Space>
							{perDay ? (
								<Text type='secondary'>
									{`Makes up to ${ perDay } record${ perDay === 1 ? '' : 's' } per ${ sourceCardName }, per day, ${ horizonDays === 1 ? 'one day' : `${ horizonDays } days` } ahead.`}
								</Text>
							) : null}
						</Space>
					</Card>
				) : null}

				{sourceCardId ? (
					<Card size='small' title='What each occurrence becomes'>
						<InputSelect
							elementName='generatorTargetCard'
							elementLabel='Create'
							value={targetCardId || undefined}
							onChange={( n, value ) => {
								setTargetCardId( value || '' );
								setLinkElementName( '' );
								setDedupeOn( [] );
							}}
							options={cardOptions}
							placeholder='Which card is the generated record?'
							allowClear={false}
						/>

						{targetCardId ? (<>
							<div style={{ marginTop: 12 }}>
								{rows.map( row => (
									<Space key={row.key} align='start' style={{ display: 'flex', marginBottom: 8 }}>
										<Select
											style={{ width: 200 }}
											value={row.name || undefined}
											placeholder='Element'
											aria-label='Element'
											options={elementOptions}
											onChange={value => patchRow( row.key, { name: value } )}
										/>
										<div style={{ flex: '1 1 auto', minWidth: 200 }}>
											{lookupTargets.get( row.name ) ? (
												// A lookup of the generated record (KPR-1104): copied from
												// the source record, or the records chosen — never typed.
												<LookupSourceField
													value={row.value}
													onChange={value => patchRow( row.key, { value } )}
													target={lookupTargets.get( row.name )}
													sources={lookupSourceOptions( {
														target: lookupTargets.get( row.name ),
														eachElements: sourceElements,
														eachCardId: sourceCardId,
														eachCardName: sourceCardName,
														getElementsForCard,
														cardsById,
													} )}
													ctx={{ eachElements: sourceElements, getElementsForCard }}
													collectionId={collectionId}
													ariaLabel={`Value for ${ elementLabelOf( lookupTargets.get( row.name ) ) || row.name }`}
													targetCardName={cardsById?.[ normalizeId( lookupTargets.get( row.name )?.options?.lookupCardId ) ]?.name || ''}
												/>
											) : (
												<TokenInput
													value={Array.isArray( row.value ) ? row.value.join( ', ' ) : row.value}
													tokens={tokens}
													placeholder='Value (templates allowed)'
													ariaLabel={`Value for ${ row.name || 'element' }`}
													onChange={value => patchRow( row.key, { value } )}
												/>
											)}
										</div>
										<Button
											type='text'
											danger
											icon={<CLOSE />}
											aria-label='Remove this value'
											onClick={() => setRows( prev => prev.filter( r => r.key !== row.key ) )}
										/>
									</Space>
								) )}
								<Button size='small' type='dashed' icon={<PLUS />} onClick={() => setRows( prev => [ ...prev, emptyRow() ] )}>
									Add value
								</Button>
							</div>

							{linkOptions.length ? (
								<div style={{ marginTop: 12 }}>
									<label htmlFor='generatorLinkElement' className='d-block'>Point the new record at its {sourceCardName}</label>
									<Select
										id='generatorLinkElement'
										style={{ width: '100%' }}
										allowClear
										value={linkElementName || undefined}
										placeholder='Lookup on the generated record'
										options={linkOptions}
										onChange={value => setLinkElementName( value || '' )}
									/>
								</div>
							) : null}

							<div style={{ marginTop: 12 }}>
								<label htmlFor='generatorDedupe' className='d-block'>One record per</label>
								<Select
									id='generatorDedupe'
									mode='multiple'
									style={{ width: '100%' }}
									value={dedupeOn}
									placeholder='Pick what identifies one record (e.g. the order and the time)'
									options={dedupeOptions}
									onChange={setDedupeOn}
								/>
								{dedupeOn.length ? null : (
									<Alert
										style={{ marginTop: 8 }}
										type='warning'
										showIcon
										message='Required'
										description='Without this, a second run writes the same work again. Pick the values that identify one record.'
									/>
								)}
							</div>
						</>) : null}
					</Card>
				) : null}


				<QueryRefusalPick
					inline
					refusal={elementPicks.refusal}
					disabled={saving}
					onPick={value => { elementPicks.pick( value ); handleSave(); }}
					onRepick={() => { elementPicks.clearRefusal(); handleSave(); }}
				/>
			</Space>
		</Modal>
	);
}
